/**
 * State for the Finder-style Files browser.
 *
 * Distinct from useVfsTree because the browser is flat-per-folder, not
 * recursive: it lists one current directory at a time and navigates into
 * others, where the tree expands every opened folder in place. The tree is the
 * right shape for a compact chat drawer; the browser is the right shape for a
 * file manager.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  api,
  type VfsFileEntry,
  type VfsMountInfo,
  type VfsSyncSource,
} from '../shared/api-client';

export interface UseVfsBrowserResult {
  mounts: VfsMountInfo[];
  syncSources: VfsSyncSource[];
  /** Children of the current directory. Empty until the first load resolves. */
  entries: VfsFileEntry[];
  loading: boolean;
  error: string | null;
  /** Set an inline error the browser shows in its error slot, without a reload. */
  setError: (msg: string | null) => void;
  /** Re-list the current directory — after an upload, delete, rename or move. */
  refresh: () => Promise<void>;
}

/**
 * @param dir    Absolute VFS path of the folder to list. `undefined` lists the
 *               mount roots (used only before a mount is chosen).
 * @param threadId  Optional chat context, as on every VFS surface.
 * @param accountId Optional account folder, for the account Files tab.
 */
export function useVfsBrowser(
  dir: string | undefined,
  threadId?: string,
  accountId?: string,
): UseVfsBrowserResult {
  const [mounts, setMounts] = useState<VfsMountInfo[]>([]);
  const [syncSources, setSyncSources] = useState<VfsSyncSource[]>([]);
  const [entries, setEntries] = useState<VfsFileEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (path: string | undefined) => {
    setLoading(true);
    const res = await api.listVfsFiles({ path, threadId, accountId });
    setLoading(false);
    if (!res.success) {
      setError(res.error.message);
      return;
    }
    setError(null);
    // A root listing (path omitted) returns the mounts as entries; a folder
    // listing returns the folder's children. Both carry mounts/syncSources, so
    // either response keeps the sidebar honest.
    setMounts(res.data.mounts);
    setSyncSources(res.data.syncSources);
    setEntries(res.data.entries);
  }, [threadId, accountId]);

  useEffect(() => {
    let cancelled = false;
    void load(dir).then(() => { if (cancelled) return; });
    return () => { cancelled = true; };
    // `dir` is the dependency: navigating to a new folder re-lists. `load` is
    // stable across renders for a given (threadId, accountId) pair.
  }, [dir, load]);

  const refresh = useCallback(async () => { await load(dir); }, [dir, load]);

  return { mounts, syncSources, entries, loading, error, setError, refresh };
}
